import { create } from 'zustand'
import type { ActivityStep, ChatRequest, MessageFeedback, Source, StatusStage, Verification } from '@/api/types'

/** Key used for a chat that has no conversation id yet. */
export const NEW_CHAT_KEY = 'new'

export const conversationKey = (id: string | null | undefined) => id ?? NEW_CHAT_KEY

/** Client-side ids are prefixed so they are never mistaken for server ids. */
export const LOCAL_PREFIX = 'local-'
export const isLocalId = (id: string) => id.startsWith(LOCAL_PREFIX)

let counter = 0
export function localId(): string {
  counter += 1
  return `${LOCAL_PREFIX}${Date.now().toString(36)}-${counter}`
}

export type LiveUserMessage = {
  clientId: string
  /** Server id, known after the `user_message` event. */
  id: string | null
  content: string
  created_at: string
}

export type LiveAssistantStatus = 'streaming' | 'complete' | 'error' | 'stopped'

export type LiveAssistantMessage = {
  clientId: string
  /** Server id, known after `done`. */
  id: string | null
  content: string
  sources: Source[]
  /** Final cited ids from `done`; null while streaming (derived from content instead). */
  cited: string[] | null
  activity: ActivityStep[]
  status: LiveAssistantStatus
  stage: StatusStage | null
  statusText: string | null
  error: string | null
  gap_flagged: boolean
  verification: Verification | null
  feedback: MessageFeedback | null
  created_at: string
}

export type TurnMode = 'send' | 'regenerate' | 'edit'

export type LiveTurn = {
  streamId: string
  conversationId: string | null
  mode: TurnMode
  /** Persisted messages from this id onward are hidden (edit/regenerate). */
  truncateAt: string | null
  request: ChatRequest
  /** null for regenerate: the existing user message stays in place. */
  user: LiveUserMessage | null
  assistant: LiveAssistantMessage
}

type ChatState = {
  /** Turns that are still streaming or failed before they could be committed. */
  turns: Record<string, LiveTurn>
  /** conversation key ('new' or id) → streamId of its live turn. */
  byConversation: Record<string, string>
  /** server message id → stable React key used while it streamed (avoids remounts). */
  clientKeys: Record<string, string>
  /** Assistant message ids waiting for their `verification` event. */
  pendingVerification: Record<string, true>
  /** Conversation ids with an open stream (sidebar activity dots). */
  activeConversations: Record<string, true>
}

export const useChatStore = create<ChatState>()(() => ({
  turns: {},
  byConversation: {},
  clientKeys: {},
  pendingVerification: {},
  activeConversations: {},
}))

// ---------------------------------------------------------------- mutators

const setState = useChatStore.setState
const getState = useChatStore.getState

export const chatStore = {
  get: getState,

  addTurn(key: string, turn: LiveTurn) {
    setState((state) => ({
      turns: { ...state.turns, [turn.streamId]: turn },
      byConversation: { ...state.byConversation, [key]: turn.streamId },
    }))
  },

  updateTurn(streamId: string, update: (turn: LiveTurn) => Partial<LiveTurn>) {
    setState((state) => {
      const turn = state.turns[streamId]
      if (!turn) return state
      return { turns: { ...state.turns, [streamId]: { ...turn, ...update(turn) } } }
    })
  },

  updateAssistant(streamId: string, update: (a: LiveAssistantMessage) => Partial<LiveAssistantMessage>) {
    setState((state) => {
      const turn = state.turns[streamId]
      if (!turn) return state
      const assistant = { ...turn.assistant, ...update(turn.assistant) }
      return { turns: { ...state.turns, [streamId]: { ...turn, assistant } } }
    })
  },

  mapConversation(key: string, streamId: string) {
    setState((state) => ({ byConversation: { ...state.byConversation, [key]: streamId } }))
  },

  /** Drop a turn and any conversation mappings still pointing at it. */
  removeTurn(streamId: string) {
    setState((state) => {
      if (!state.turns[streamId]) return state
      const turns = { ...state.turns }
      delete turns[streamId]
      const byConversation = Object.fromEntries(
        Object.entries(state.byConversation).filter(([, id]) => id !== streamId),
      )
      return { turns, byConversation }
    })
  },

  /** Forget the new-chat mapping (New chat pressed). The turn itself keeps running. */
  clearNewChat() {
    setState((state) => {
      const streamId = state.byConversation[NEW_CHAT_KEY]
      if (!streamId) return state
      const byConversation = { ...state.byConversation }
      delete byConversation[NEW_CHAT_KEY]
      const turn = state.turns[streamId]
      // A failed turn that never got a conversation can't be reached any more.
      if (turn && !turn.conversationId && turn.assistant.status !== 'streaming') {
        const turns = { ...state.turns }
        delete turns[streamId]
        return { byConversation, turns }
      }
      return { byConversation }
    })
  },

  setClientKey(serverId: string, clientKey: string) {
    setState((state) =>
      state.clientKeys[serverId] === clientKey ? state : { clientKeys: { ...state.clientKeys, [serverId]: clientKey } },
    )
  },

  setPendingVerification(messageId: string, pending: boolean) {
    setState((state) => {
      if (pending === Boolean(state.pendingVerification[messageId])) return state
      const pendingVerification = { ...state.pendingVerification }
      if (pending) pendingVerification[messageId] = true
      else delete pendingVerification[messageId]
      return { pendingVerification }
    })
  },

  setConversationActive(conversationId: string, active: boolean) {
    setState((state) => {
      if (active === Boolean(state.activeConversations[conversationId])) return state
      const activeConversations = { ...state.activeConversations }
      if (active) activeConversations[conversationId] = true
      else delete activeConversations[conversationId]
      return { activeConversations }
    })
  },
}

// ---------------------------------------------------------------- selectors

export function selectTurn(state: ChatState, key: string): LiveTurn | undefined {
  const streamId = state.byConversation[key]
  return streamId ? state.turns[streamId] : undefined
}

export function selectIsStreaming(state: ChatState, key: string): boolean {
  return selectTurn(state, key)?.assistant.status === 'streaming'
}
